import { getAxios } from '../axios';
import {
  ENDPOINT_ADMIN_GROUPS,
  ENDPOINT_ADMIN_ACLS, ENDPOINT_ADMIN_GROUPS_ACLS
} from '../../config/endpoints';
import {
  getTokenHeaders,
  addParam,
  errorCatch,
  getNotificationId,
} from '../helper';
import { Messages } from '../../config';
import { displayNotification } from '../../store/actions/notification';
import { NOTIFICATION_SUCCESS } from '../../store/actions/types';
import { store } from '../../store/store';

/**
 * Get list of admin groups
 * @param {object} payload
 * @param {boolean} [payload.disablePagination]
 * @param {boolean} payload.hideNotification
 * @param {object} payload.pagination
 * @param {number} payload.pagination.page
 * @param {number} payload.pagination.per_page
 * @param {string} payload.pagination.sort_by
 * @param {string} payload.pagination.sort
 * @param {string} payload.pagination.q
 * @param {string} payload.page_context
 * @returns {Promise<T>}
 */
export function getGroups(payload) {
  let headersAndParams = getTokenHeaders( payload.pagination ? payload.pagination : payload.disablePagination ? {} : { per_page: 15 } );
  return getAxios(payload.hideNotification)
    .get(
      addParam( ENDPOINT_ADMIN_GROUPS, {
        '{group}': '',
      } ),
      {
        headers: headersAndParams.headers,
        params: {
          ...headersAndParams.params,
          disablePagination: payload.disablePagination,
        },
      }

    )
    .then(response => {
      return response && response.data ? response.data : null
    })
    .catch(
      e => {
        errorCatch(e, {
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        });

        return e.response;
      }
    );
}

/**
 * Get details of admin group
 * @param {object} payload
 * @param {string} payload.groupId
 * @param {string} payload.notificationId
 * @returns {Promise<T>}
 */
export function getGroupDetails(payload) {
  return getAxios()
    .get(
      addParam( ENDPOINT_ADMIN_GROUPS, {
        "{group}": payload.groupId,
      } ),
      getTokenHeaders(payload.pagination)
    )
    .then( (response) => {

      return response && response.data ? response.data : null;

    } )
    .catch( (e) => {
      errorCatch( e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      }, getNotificationId( payload ) );

      return e.response;
    } );
}

/**
 * Create an admin group
 * @param {object} payload
 * @param {string} payload.notificationId
 * @param {object} payload.data
 * @param {string} payload.data.name
 * @returns {Promise<T>}
 */
export function createGroup(payload) {
  return getAxios()
    .post(
      addParam(
        ENDPOINT_ADMIN_GROUPS,
        {
          "{group}": "",
        } ),
      payload.data,
      getTokenHeaders()
    ).then(
      response => {
        if (response && response.data) {
          store.dispatch( displayNotification( NOTIFICATION_SUCCESS, Messages.CREATE_RESOURCE_SUCCESS, payload.notificationId || null ) );
          return response.data;
        }

        return null;
      }
    ).catch(
      e => {
        errorCatch( e, {
          400: 'concat',
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        }, getNotificationId( payload ) );
        return e.response;
      }
    );
}

/**
 * Edit an group
 * @param {object} payload
 * @param {string} payload.notificationId
 * @param {object} payload.data
 * @param {number} payload.data.id
 * @returns {Promise<T>}
 */
export function updateGroup(payload) {
  return getAxios()
    .put(
      addParam( ENDPOINT_ADMIN_GROUPS, {
        "{group}": payload.data.id.toString(),
      } ),
      payload.data,
      getTokenHeaders()
    )
    .then( (response) => {
      if (response && response.data) {
        store.dispatch(
          displayNotification(
            NOTIFICATION_SUCCESS,
            Messages.UPDATE_RESOURCE_SUCCESS,
            payload.notificationId
          )
        );

        return response.data;
      }

      return null;
    } )
    .catch( (e) => {
      errorCatch( e, {
        400: 'concat',
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      } );

      return e.response;
    } );
}


/**
 * Delete an admin group
 * @param {object} payload
 * @param {string} payload.groupId
 * @param {string} payload.notificationId
 * @returns {Promise<T>}
 */
 export function deleteGroup(payload) {

  return getAxios( false ).delete(
    addParam(
      ENDPOINT_ADMIN_GROUPS,
      {
        '{group}': payload.groupId,
      }
    ),
    getTokenHeaders()
  )
    .then(
      response => {
        if (response && response.status === 204) {
          store.dispatch( displayNotification( NOTIFICATION_SUCCESS, Messages.DELETE_RESOURCE_SUCCESS, payload.notification || null ) );
          return response;
        }

        return null;
      }
    )
    .catch(
      e => {
        errorCatch( e, {
          400: 'concat',
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        }, getNotificationId(payload) );

        return e.response;
      }
    );
}

/**
 * Update an admin group with users
 * @param {object} payload
 * @param {object} payload.data
 * @param {object[]} payload.data.groups
 * @param {string} payload.data.groups.id
 * @param {number[]} payload.data.groups.users
 * @param {string} payload.notificationId
 * @returns {Promise<T>}
 */
export function updateGroupWithUsers(payload) {

  return getAxios().put(
    addParam(
      ENDPOINT_ADMIN_GROUPS,
      {
        '{group}': 'users'
      }
    ),
    payload.data,
    getTokenHeaders()
  )
    .then(
      response => {
        if (response && response.data) {
          store.dispatch( displayNotification( NOTIFICATION_SUCCESS, Messages.UPDATE_RESOURCE_SUCCESS, payload.notification || null ) );

          return response.data;
        }

        return null;
      }
    )
    .catch(
      e => {
        errorCatch(e, {
          400: 'concat',
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
        }, getNotificationId(payload));

        return e.response;
      }
    );
}

/**
 * Get ACL list
 * @returns {Promise<T>}
 */
export function getAdminAcls(payload) {

  return getAxios().get(
    addParam(
      ENDPOINT_ADMIN_ACLS,
    ),
    getTokenHeaders()
  )
    .then( response => {
      const userLanguage = store.getState().locale.lang;

      if (response && response.data && response && response.data.data) {
        response.data.data.forEach(
          item => {
            item.translations.forEach(
              translation => {
                if (translation.language.short_name === userLanguage) {
                  item.translatedName = translation.name;
                }
              }
            )
          }
        )
      }

      return response && response.data ? response.data : null
    } )
    .catch(
      e => {
        errorCatch( e, {
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        }, getNotificationId( payload ) );

        return e.response;
      }
    );
}

/**
 * Edit an group
 * @param {object} payload
 * @param {string} payload.notificationId
 * @param {object} payload.data
 * @param {number} payload.data.id
 * @returns {Promise<T>}
 */
export function updateGroupWithAcls(payload) {
  return getAxios()
    .put(
      addParam( ENDPOINT_ADMIN_GROUPS_ACLS, {
        "{group}": payload.data.id.toString(),
      } ),
      payload.data,
      getTokenHeaders()
    )
    .then( (response) => {
      if (response && response.data) {
        store.dispatch(
          displayNotification(
            NOTIFICATION_SUCCESS,
            Messages.UPDATE_RESOURCE_SUCCESS,
            payload.notificationId
          )
        );

        return response.data;
      }

      return null;
    } )
    .catch( (e) => {
      errorCatch( e, {
        400: 'concat',
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      } );

      return e.response;
    } );
}
